// 备货录入 (Phase 3) — the nightly two-lane plan that feeds the central kitchen.
// LEFT 订单录入 (pre-orders) · RIGHT 现货录入 (planned walk-in stock). A shared
// 录入完成 footer materializes (orders+stock)@CK_NY → 调拨 → store, once per
// (date, store), and notifies 后厨. Disabled until BOTH lanes are marked done
// for all three stores, with a "还差 X" hint so it's never a silent dead button.
// Mobile (<820px): top 订单/现货 tabs with a pending-dot on the unfinished lane.
// 店长/管理员 only.

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api.ts';
import { displayName } from '../lib/products.ts';
import { torontoToday } from '../lib/torontoTime.js';
import { isElevated } from '../lib/auth.ts';
import { useMediaQuery } from '../lib/useMediaQuery.js';
import { Icon } from '../components/Icon.jsx';
import { Toast } from '../components/atoms.jsx';
import { confirmDialog } from '../lib/confirm.js';

const STORES = ['NY', 'DT', 'RH'];
const PAY = [{ v: 'unpaid', l: '未付' }, { v: 'deposit', l: '订金' }, { v: 'paid', l: '已付' }];
const CHANNELS = [{ v: '', l: '渠道' }, { v: 'phone', l: '电话' }, { v: 'walkin', l: '到店' }, { v: 'wechat', l: '微信' }];

function plusDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
const blankOrder = (store) => ({ store, productId: '', qty: 1, pickupTime: '', customerName: '', customerPhone: '', channel: '', payStatus: 'unpaid', depositAmt: '' });
const blankStock = (store) => ({ store, productId: '', qty: 1 });

export const PrepEntry = ({ user }) => {
  const elevated = isElevated(user);
  const isMobile = useMediaQuery('(max-width: 820px)');
  const [date, setDate] = useState(() => plusDays(torontoToday(), 1));
  const [products, setProducts] = useState([]);

  // orders lane
  const [orders, setOrders] = useState([]);
  const [orderRow, setOrderRow] = useState(() => blankOrder('NY'));
  const [showMore, setShowMore] = useState(false);

  // stock lane
  const [plan, setPlan] = useState([]);
  const [lanes, setLanes] = useState({});
  const [stockRow, setStockRow] = useState(() => blankStock('NY'));

  const [tab, setTab] = useState('orders');     // mobile lane tab
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { api.listProducts({}).then(setProducts).catch(() => {}); }, []);

  function reloadOrders() {
    api.listOrders(date).then(r => setOrders(r.orders)).catch(() => {});
  }
  function reloadPrep() {
    api.loadPrep(date).then(d => { setPlan(d.plan || []); setLanes(d.lanes || {}); }).catch(() => {});
  }
  useEffect(() => { reloadOrders(); reloadPrep(); /* eslint-disable-next-line */ }, [date]);

  const ordersByStore = useMemo(() => {
    const m = { NY: [], DT: [], RH: [] };
    for (const o of orders) (m[o.store] || (m[o.store] = [])).push(o);
    return m;
  }, [orders]);

  const productById = useMemo(() => {
    const m = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  const planByStore = useMemo(() => {
    const m = { NY: [], DT: [], RH: [] };
    for (const r of plan) (m[r.store] || (m[r.store] = [])).push(r);
    return m;
  }, [plan]);

  const productOptions = useMemo(() => {
    const groups = {};
    for (const p of products) (groups[p.category || '其他'] ||= []).push(p);
    return groups;
  }, [products]);

  const laneOf = (s) => lanes[s] || { ordersDone: false, stockDone: false, materialized: false };
  const anyMaterialized = STORES.some(s => laneOf(s).materialized);
  const allMaterialized = STORES.every(s => laneOf(s).materialized);
  const allLanesDone = STORES.every(s => { const l = laneOf(s); return l.ordersDone && l.stockDone; });

  // "还差" hint — list every (store, lane) not yet marked done.
  const missing = useMemo(() => {
    const out = [];
    for (const s of STORES) {
      const l = laneOf(s);
      if (!l.ordersDone) out.push(`${s} 订单`);
      if (!l.stockDone) out.push(`${s} 现货`);
    }
    return out;
    // eslint-disable-next-line
  }, [lanes]);

  // ---- orders lane actions --------------------------------------------------
  async function addOrder() {
    if (!elevated) return setToast({ kind: 'error', msg: '仅 店长/管理员 可录入' });
    if (!orderRow.productId || !(orderRow.qty > 0)) return setToast({ kind: 'error', msg: '请选择口味并填写数量' });
    setBusy(true);
    try {
      await api.createOrder({
        pickup_date: date, store: orderRow.store, product_id: orderRow.productId, qty: Number(orderRow.qty),
        pickup_time: orderRow.pickupTime || null, customer_name: orderRow.customerName || null,
        customer_phone: orderRow.customerPhone || null, channel: orderRow.channel || null,
        pay_status: orderRow.payStatus, deposit_amt: orderRow.depositAmt === '' ? null : Number(orderRow.depositAmt)
      });
      setOrderRow(r => ({ ...blankOrder(r.store) }));
      reloadOrders();
    } catch (e) { setToast({ kind: 'error', msg: e?.message || '添加失败' }); }
    finally { setBusy(false); }
  }
  async function removeOrder(o) {
    if (!await confirmDialog({ title: '删除订单', kind: 'danger', confirmLabel: '删除', message: `删除订单：${o.store} · ${o.productName} ×${o.qty}？` })) return;
    try { await api.deleteOrder(o.id); reloadOrders(); } catch (e) { setToast({ kind: 'error', msg: e?.message || '删除失败' }); }
  }
  async function toggleFulfill(o) {
    try {
      if (o.status === 'fulfilled') await api.unfulfillOrder(o.id); else await api.fulfillOrder(o.id);
      reloadOrders();
    } catch (e) { setToast({ kind: 'error', msg: e?.message || '操作失败' }); }
  }

  // ---- stock lane actions ---------------------------------------------------
  // Selecting a product already in this (store, day) plan pre-fills its current
  // qty, so re-entering it reads as "editing the existing number", not "adding a
  // second one that vanished" (the plan is one qty per product — upsert replaces).
  function pickStockProduct(pid) {
    const existing = (planByStore[stockRow.store] || []).find(r => r.product_id === pid);
    setStockRow(r => ({ ...r, productId: pid, qty: existing ? existing.planned_qty : r.qty }));
  }
  async function addStock() {
    if (!elevated) return setToast({ kind: 'error', msg: '仅 店长/管理员 可录入' });
    if (!stockRow.productId || !(Number(stockRow.qty) >= 0)) return setToast({ kind: 'error', msg: '请选择口味并填写数量' });
    const existing = (planByStore[stockRow.store] || []).find(r => r.product_id === stockRow.productId);
    const p = productById[stockRow.productId];
    const qty = Number(stockRow.qty);
    setBusy(true);
    try {
      await api.upsertPrep({ plan_date: date, store: stockRow.store, product_id: stockRow.productId, planned_qty: qty });
      setStockRow(r => ({ ...blankStock(r.store) }));
      reloadPrep();
      setToast({ kind: 'success', msg: existing ? `已更新 ${displayName(p)} → ${qty}` : `已添加 ${displayName(p)} ×${qty}` });
    } catch (e) { setToast({ kind: 'error', msg: e?.message || '添加失败' }); }
    finally { setBusy(false); }
  }
  async function removeStock(r) {
    try { await api.deletePrep(r.id); reloadPrep(); } catch (e) { setToast({ kind: 'error', msg: e?.message || '删除失败' }); }
  }

  // ---- lane-done toggles ----------------------------------------------------
  // 订单 lane done keeps order_days confirmed (so the 后厨 banner stays in sync)
  // AND flips prep_days.orders_done (which gates 录入完成).
  async function toggleOrdersDone(store) {
    const done = !laneOf(store).ordersDone;
    try {
      if (done) await api.confirmOrderDay(date, store); else await api.unconfirmOrderDay(date, store);
      await api.setPrepLaneDone({ date, store, lane: 'orders', done });
      reloadOrders(); reloadPrep();
    } catch (e) { setToast({ kind: 'error', msg: e?.message || '操作失败' }); }
  }
  async function toggleStockDone(store) {
    const done = !laneOf(store).stockDone;
    try {
      await api.setPrepLaneDone({ date, store, lane: 'stock', done });
      reloadPrep();
    } catch (e) { setToast({ kind: 'error', msg: e?.message || '操作失败' }); }
  }

  // ---- 录入完成 / 撤销 ------------------------------------------------------
  async function complete() {
    setCompleting(true);
    try {
      const r = await api.completePrep(date);
      setToast({ kind: 'success', msg: `已通知后厨 · 备货 ${r.products} 项已下达` });
      reloadPrep();
    } catch (e) {
      // Both the 409 (not-ready) and 422 (a store sold that day) return { error, stores }.
      const stores = e?.payload?.stores;
      setToast({ kind: 'error', msg: stores?.length ? `${e?.message || '还差未录完的店'}：${stores.join(' / ')}` : (e?.message || '录入完成失败') });
    } finally { setCompleting(false); }
  }
  async function uncomplete(store) {
    if (!await confirmDialog({ title: `撤销 ${store} 录入完成`, confirmLabel: '撤销并解锁', message: `会解锁以便修改 — 已下发的库存保留、后厨照常显示，后厨会收到修改通知，重新「录入完成」后覆盖更新。仅当该店当天还没有售出时可撤销。` })) return;
    try { await api.uncompletePrep(date, store); reloadPrep(); reloadOrders(); setToast({ kind: 'success', msg: `${store} 已解锁，可修改 · 已通知后厨` }); }
    catch (e) { setToast({ kind: 'error', msg: e?.message || '撤销失败' }); }
  }

  const isTomorrow = date === plusDays(torontoToday(), 1);

  // ---- lane renderers -------------------------------------------------------
  const productPicker = (val, onChange, label) => (
    <select className="oe-f oe-prod" value={val} onChange={onChange} aria-label={label}>
      <option value="">选择口味 / 规格…</option>
      {Object.entries(productOptions).map(([cat, ps]) => (
        <optgroup key={cat} label={cat}>
          {ps.map(p => <option key={p.id} value={p.id}>{displayName(p)}</option>)}
        </optgroup>
      ))}
    </select>
  );

  const renderOrdersLane = () => {
    return (
      <section className="prep-pane">
        <div className="prep-pane-head"><Icon name="clipboard" /> 订单录入 <span className="prep-pane-sub">次日预订</span></div>
        <div className="vava-card oe-add">
          <div className="oe-add-row">
            <select className="oe-f oe-store" value={orderRow.store} onChange={e => setOrderRow({ ...orderRow, store: e.target.value })} aria-label="门店">
              {STORES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {productPicker(orderRow.productId, e => setOrderRow({ ...orderRow, productId: e.target.value }), '口味')}
            <input className="oe-f oe-qty" type="number" min="1" value={orderRow.qty} onChange={e => setOrderRow({ ...orderRow, qty: e.target.value })} aria-label="数量" />
            <input className="oe-f oe-time" type="time" value={orderRow.pickupTime} onChange={e => setOrderRow({ ...orderRow, pickupTime: e.target.value })} aria-label="取货时间" />
            <button className="btn btn-primary" onClick={addOrder} disabled={busy || !elevated}><Icon name="plus" /> 添加</button>
          </div>
          <button type="button" className="oe-more-toggle" onClick={() => setShowMore(v => !v)}>
            {showMore ? '▾' : '▸'} 更多（顾客 / 渠道 / 订金）
          </button>
          {showMore && (
            <div className="oe-more">
              <input className="oe-f" placeholder="顾客姓名" value={orderRow.customerName} onChange={e => setOrderRow({ ...orderRow, customerName: e.target.value })} />
              <input className="oe-f" placeholder="电话" value={orderRow.customerPhone} onChange={e => setOrderRow({ ...orderRow, customerPhone: e.target.value })} />
              <select className="oe-f" value={orderRow.channel} onChange={e => setOrderRow({ ...orderRow, channel: e.target.value })}>
                {CHANNELS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
              <select className="oe-f" value={orderRow.payStatus} onChange={e => setOrderRow({ ...orderRow, payStatus: e.target.value })}>
                {PAY.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
              <input className="oe-f oe-qty" type="number" min="0" step="0.01" placeholder="订金$" value={orderRow.depositAmt} onChange={e => setOrderRow({ ...orderRow, depositAmt: e.target.value })} />
            </div>
          )}
        </div>

        <div className="prep-stores">
          {STORES.map(store => {
            const l = laneOf(store);
            const list = ordersByStore[store] || [];
            return (
              <div key={store} className="vava-card oe-store-col">
                <div className="oe-store-head">
                  <span className="oe-store-name">{store}</span>
                  <span className="oe-store-count mono">{list.length} 单 · {list.reduce((s, o) => s + o.qty, 0)} 个</span>
                  {l.materialized
                    ? <span className="oe-confirmed"><Icon name="check" /> 已完成</span>
                    : l.ordersDone
                      ? <button className="prep-lane-toggle is-done" onClick={() => toggleOrdersDone(store)} disabled={!elevated}><Icon name="check" /> 订单已录完</button>
                      : <button className="prep-lane-toggle" onClick={() => toggleOrdersDone(store)} disabled={!elevated}>标记订单完成</button>}
                </div>
                {list.length === 0 ? <div className="oe-empty">还没有订单</div> : list.map(o => (
                  <div key={o.id} className={`oe-order${o.status === 'fulfilled' ? ' is-done' : ''}`}>
                    <div className="oe-order-main">
                      <span className="oe-order-prod">{o.productName} <b>×{o.qty}</b></span>
                      <span className="oe-order-meta mono">
                        {o.pickupTime ? o.pickupTime + ' · ' : ''}{PAY.find(p => p.v === o.payStatus)?.l}{o.depositAmt ? ` $${o.depositAmt}` : ''}{o.customerName ? ' · ' + o.customerName : ''}
                      </span>
                    </div>
                    <div className="oe-order-actions">
                      <button className="oe-act" title={o.status === 'fulfilled' ? '撤销取货' : '标记已取货'} onClick={() => toggleFulfill(o)}>
                        {o.status === 'fulfilled' ? '↩' : '✓取货'}
                      </button>
                      <button className="oe-act oe-act-danger" title="删除" onClick={() => removeOrder(o)}><Icon name="x" /></button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  const renderStockLane = () => {
    return (
      <section className="prep-pane">
        <div className="prep-pane-head"><Icon name="box" /> 现货录入 <span className="prep-pane-sub">明日上架现货</span></div>
        <div className="vava-card oe-add">
          <div className="oe-add-row">
            <select className="oe-f oe-store" value={stockRow.store} onChange={e => setStockRow({ ...stockRow, store: e.target.value })} aria-label="门店">
              {STORES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {productPicker(stockRow.productId, e => pickStockProduct(e.target.value), '口味')}
            <input className="oe-f oe-qty" type="number" min="0" value={stockRow.qty} onChange={e => setStockRow({ ...stockRow, qty: e.target.value })} aria-label="数量" />
            <button className="btn btn-primary" onClick={addStock} disabled={busy || !elevated}><Icon name="plus" /> 添加</button>
          </div>
        </div>

        <div className="prep-stores">
          {STORES.map(store => {
            const l = laneOf(store);
            const list = planByStore[store] || [];
            const totalQty = list.reduce((s, r) => s + r.planned_qty, 0);
            return (
              <div key={store} className="vava-card oe-store-col">
                <div className="oe-store-head">
                  <span className="oe-store-name">{store}</span>
                  <span className="oe-store-count mono">{list.length} 项 · {totalQty} 个</span>
                  {l.materialized
                    ? <span className="oe-confirmed"><Icon name="check" /> 已完成</span>
                    : l.stockDone
                      ? <button className="prep-lane-toggle is-done" onClick={() => toggleStockDone(store)} disabled={!elevated}><Icon name="check" /> 现货已录完</button>
                      : <button className="prep-lane-toggle" onClick={() => toggleStockDone(store)} disabled={!elevated}>标记现货完成</button>}
                </div>
                {list.length === 0 ? (
                  <div className="oe-empty">{l.stockDone ? '今日不备现货' : '还没有现货 · 标记完成即「今日不备」'}</div>
                ) : list.map(r => {
                  const p = productById[r.product_id];
                  return (
                    <div key={r.id} className="prep-stock-row">
                      <span className="name">{p ? displayName(p) : r.product_id} <b>×{r.planned_qty}</b></span>
                      {!l.materialized && (
                        <button className="oe-act oe-act-danger" title="删除" onClick={() => removeStock(r)} disabled={!elevated}><Icon name="x" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  const ordersTabPending = STORES.some(s => !laneOf(s).ordersDone) && !allMaterialized;
  const stockTabPending = STORES.some(s => !laneOf(s).stockDone) && !allMaterialized;

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">备货录入</h1>
          <div className="vava-page-meta">订单 + 现货两栏录入 · 录入完成自动备货中央厨房并分发到三店 · 通知后厨开始生产</div>
        </div>
        <div className="oe-datebox">
          <label className="oe-date-label">生产日</label>
          <input type="date" className="oe-date" value={date} onChange={e => setDate(e.target.value)} />
          {isTomorrow && <span className="oe-tag">明天</span>}
        </div>
      </div>

      {isMobile ? (
        <>
          <div className="ks-tabs prep-tabs">
            <button className={`ks-tab${tab === 'orders' ? ' is-active' : ''}`} onClick={() => setTab('orders')}>
              订单{ordersTabPending && <span className="nav-pending-dot" title="订单未录完" />}
            </button>
            <button className={`ks-tab${tab === 'stock' ? ' is-active' : ''}`} onClick={() => setTab('stock')}>
              现货{stockTabPending && <span className="nav-pending-dot" title="现货未录完" />}
            </button>
          </div>
          {tab === 'orders' ? renderOrdersLane() : renderStockLane()}
        </>
      ) : (
        <div className="prep-panes">
          {renderOrdersLane()}
          {renderStockLane()}
        </div>
      )}

      <div className="prep-foot">
        {allMaterialized ? (
          <div className="prep-foot-done">
            <span className="oe-foot-confirmed"><Icon name="check" /> {date} 三店已录入完成 · 已通知后厨</span>
            <div className="prep-undo-row">
              {STORES.map(s => <button key={s} className="oe-act" onClick={() => uncomplete(s)} disabled={!elevated}>撤销 {s}</button>)}
            </div>
          </div>
        ) : (
          <>
            <button className="btn btn-primary" onClick={complete} disabled={!elevated || completing || !allLanesDone}>
              <Icon name="check" /> {completing ? '处理中…' : '录入完成（通知后厨）'}
            </button>
            {!allLanesDone && <div className="prep-foot-hint">还差：{missing.join(' · ')}</div>}
            {anyMaterialized && (
              <div className="prep-undo-row">
                {STORES.filter(s => laneOf(s).materialized).map(s => <button key={s} className="oe-act" onClick={() => uncomplete(s)} disabled={!elevated}>撤销 {s}</button>)}
              </div>
            )}
          </>
        )}
      </div>

      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
};
